"use client"
import { BodySmall, Heading1, Heading4 } from "@/components/text"
import TextLink from "@/components/text/TextLink"
import { PauseIcon, PlayIcon } from "@heroicons/react/20/solid"
import Image from "next/image"
import Link from "next/link"
import { SetStateAction, useCallback, useEffect, useState } from "react"

type SliderItem = {
  image: string
  title: React.ReactNode
  body: React.ReactNode
}

const contentSlider: SliderItem[] = [
  {
    image: "/goodshift/img/home/carousel-1.png",
    title: "Vind goede doelen die bij jou passen",
    body: "Ontdek en kies uit meer dan 100 goede doelen en zoek gemakkelijk op thema's of kies een bundel",
  },
  {
    image: "/goodshift/img/home/carousel-2.png",
    title: (
      <>
        Doneer en verdien{" "}
        <Link
          href="/goodshift-punten"
          className="text-purple hover:text-purple-800 underline focus-visible:outline focus-visible:rounded-lg focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2 active:scale-95">
          GoodShift punten
        </Link>
      </>
    ),
    body: (
      <>
        Start, stop of wijzig jouw donaties wanneer jij wilt en maak een <TextLink href="/acties">actie</TextLink> aan
        en geef samen met vrienden en familie
      </>
    ),
  },
  {
    image: "/goodshift/img/home/carousel-3.png",
    title: "Zie de impact die we samen maken",
    body: "Ontvang updates via Good news over de impact van jouw donaties en bekijk al je donaties overzichtelijk op één plek",
  },
]

const HowItWorksAnimated: React.FC = () => {
  const [index, setIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (isPaused) return // Don't start interval if paused
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % contentSlider.length)
    }, 4200)
    return () => clearInterval(interval)
  }, [index, isPaused])
  // Added index as a deppendecy to make the interval will automatically reset when the user calls handleClick

  const handleClick = useCallback((i: SetStateAction<number>) => {
    setIsPaused(true)
    setIndex(i)
  }, [])

  const handlePause = useCallback(() => {
    setIsPaused(!isPaused)
  }, [isPaused])

  return (
    <div className="relative isolate overflow-hidden bg-white lg:overflow-visible lg:px-0">
      <Heading1 className="text-center mb-28 max-w-3xl mx-auto">
        Zo werkt doneren aan goede doelen met GoodShift
      </Heading1>
      <div className="mx-auto flex flex-col-reverse lg:flex-row max-w-2xl gap-y-16 gap-x-8 xl:gap-16 lg:max-w-7xl items-center contentSlider-center relative">
        <div className="flex-1 min-h-[617px] min-w-[100%] md:min-w-[617px] max-h-[617px] max-w-[620px]  relative rounded-xl bg-red">
          {contentSlider.map((item, i) => (
            <Image
              key={i}
              width={620}
              height={620}
              alt={`image of the GoodShift app`}
              src={item.image}
              sizes="(max-width: 1024px) 100vw, 620px"
              priority={i === 0}
              loading={i === 0 ? undefined : "lazy"}
              className={`absolute top-0 left-0 w-full h-full rounded-xl object-cover transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
            />
          ))}
          <div
            className="absolute h-8 w-8 right-4 top-4 text-white opacity-70 hover:opacity-100 cursor-pointer"
            onClick={handlePause}>
            {isPaused ? <PlayIcon /> : <PauseIcon />}
          </div>
        </div>
        <div>
          {contentSlider.map((item, i) => (
            <div
              onMouseEnter={() => (index === i ? setIsPaused(true) : null)}
              onMouseLeave={() => (index === i ? setIsPaused(false) : null)}
              key={i}
              onClick={() => handleClick(i)}
              className={`my-[2.5rem] transition-all duration-1000 cursor-pointer border-l-2 pl-[2rem] ${i === index ? "border-red" : "border-transparent"}`}>
              <Heading4>{item.title}</Heading4>
              <BodySmall>{item.body}</BodySmall>
            </div>
          ))}
        </div>
        <div
          className=" hidden absolute h-8 w-8 right-2 top-2 text-gray-50 hover:text-purple cursor-pointer"
          onClick={handlePause}>
          {isPaused ? <PlayIcon /> : <PauseIcon />}
        </div>
      </div>
    </div>
  )
}

export default HowItWorksAnimated
